/**
 * Pure logic for wizard step 2 ("Pris & etappmål"): parse amounts, compute the
 * allocation, validate and build the `PUT /contracts/:id/milestone-plan`
 * payload. Mirrors the rules of the contract page's guided plan and the API
 * (`replaceMilestonePlan`, `rot-allocation.ts`).
 *
 * - top_down: the company enters the tender sum and the amount after ROT and
 *   distributes the amount after ROT over the stages (kronor or percent).
 * - bottom_up: every stage carries its own gross amount and ROT deduction; the
 *   contract totals are their sums.
 *
 * All amounts are whole kronor in the UI.
 *
 * Autofill (top_down, two or more stages): an EMPTY amount/percent on the last
 * stage means "the rest". The remainder is shown greyed in the field and counts
 * everywhere (sum, status, validation, payload). A value the user typed is never
 * changed; clearing the field brings the autofill back. The autofill belongs to
 * the position: when stages are added or moved, an autofilled last stage first
 * gets its value written in (it becomes the user's), and the stage that ends up
 * last is autofilled only if its field is empty.
 */

export type PricingMode = 'top_down' | 'bottom_up';
export type DistributionMode = 'amount' | 'percent';

export interface PlanRow {
  /** Stable React key; not sent to the API. */
  key: string;
  /** Server id once the stage is saved. */
  id?: string;
  description: string;
  /** top_down: amount after ROT. bottom_up: gross amount before ROT. */
  amount: string;
  /** bottom_up only: the stage's ROT deduction. */
  rotAmount: string;
  /** top_down + percent distribution only. */
  percent: string;
  endDate: string;
}

export interface PlanState {
  pricingMode: PricingMode;
  distribution: DistributionMode;
  /** top_down: tender sum before ROT. */
  total: string;
  /** top_down: amount after ROT; empty means "same as the tender sum" (no ROT). */
  afterRot: string;
  rows: PlanRow[];
}

export const MAX_PLAN_ROWS = 50;

let rowCounter = 0;
export const newPlanRow = (): PlanRow => ({
  key: `row-${Date.now().toString(36)}-${(rowCounter += 1)}`,
  description: '',
  amount: '',
  rotAmount: '',
  percent: '',
  endDate: '',
});

export const initialPlanState = (): PlanState => ({
  pricingMode: 'top_down',
  distribution: 'amount',
  total: '',
  afterRot: '',
  rows: [newPlanRow()],
});

/** "185 000" / "185000" → 185000; empty → 0; anything unparsable → NaN. */
export const parseKronor = (value: string): number => {
  const normalized = String(value ?? '').replace(/[\s ]/g, '').replace(',', '.');
  if (normalized === '') return 0;
  return /^-?\d+(\.\d+)?$/.test(normalized) ? Number(normalized) : NaN;
};

export const parsePercent = (value: string): number => {
  const normalized = String(value ?? '').replace(/[\s %]/g, '').replace(',', '.');
  if (normalized === '') return 0;
  return /^\d+(\.\d+)?$/.test(normalized) ? Number(normalized) : NaN;
};

const isWhole = (value: number) => Number.isFinite(value) && Number.isInteger(value);

export const formatKronor = (value: number) =>
  Number.isFinite(value) ? Math.round(value).toLocaleString('sv-SE') : '–';

const roundPercent = (value: number) => Math.round(value * 100) / 100;
const formatPercentInput = (value: number) => String(roundPercent(value)).replace('.', ',');

const activeField = (state: PlanState): 'amount' | 'percent' =>
  state.distribution === 'percent' ? 'percent' : 'amount';

/** The amount after ROT the stages share (top_down); NaN while not a number. */
const topDownTarget = (state: PlanState) => {
  const gross = parseKronor(state.total);
  return state.afterRot.trim() === '' ? gross : parseKronor(state.afterRot);
};

/**
 * What the empty last stage is autofilled with, as the field shows it ("17",
 * "67 500"), or null when it is not autofilled: bottom_up, a single stage, a
 * value the user typed, nothing left to distribute, or amounts that do not parse.
 */
export function autofillRemainder(state: PlanState): string | null {
  if (state.pricingMode !== 'top_down' || state.rows.length < 2) return null;
  const field = activeField(state);
  const last = state.rows[state.rows.length - 1];
  if (last[field].trim() !== '') return null;
  const prior = state.rows.slice(0, -1);
  if (field === 'percent') {
    const percents = prior.map(row => parsePercent(row.percent));
    if (percents.some(value => !Number.isFinite(value))) return null;
    const left = roundPercent(100 - percents.reduce((sum, value) => sum + value, 0));
    return left > 0 ? formatPercentInput(left) : null;
  }
  const target = topDownTarget(state);
  if (!isWhole(target) || target <= 0) return null;
  const amounts = prior.map(row => parseKronor(row.amount));
  if (amounts.some(value => !isWhole(value))) return null;
  const left = target - amounts.reduce((sum, value) => sum + value, 0);
  return left > 0 ? formatKronor(left) : null;
}

/** The plan with the autofilled remainder written into the last stage. */
export function withAutofill(state: PlanState): PlanState {
  const remainder = autofillRemainder(state);
  if (remainder === null) return state;
  const field = activeField(state);
  return {
    ...state,
    rows: state.rows.map((row, index) => (index === state.rows.length - 1 ? { ...row, [field]: remainder } : row)),
  };
}

/** Adds a stage last; an autofilled last stage keeps its value as the user's. */
export function addPlanRow(state: PlanState, row: PlanRow = newPlanRow()): PlanState {
  if (state.rows.length >= MAX_PLAN_ROWS) return state;
  const filled = withAutofill(state);
  return { ...filled, rows: [...filled.rows, row] };
}

/**
 * Moves a stage one step up (-1) or down (+1). The autofill follows the
 * position: an autofilled last stage keeps its value as the user's, and the
 * stage that ends up last is autofilled only if its field is empty.
 */
export function movePlanRow(state: PlanState, key: string, direction: -1 | 1): PlanState {
  const from = state.rows.findIndex(row => row.key === key);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= state.rows.length) return state;
  const filled = withAutofill(state);
  const rows = [...filled.rows];
  [rows[from], rows[to]] = [rows[to], rows[from]];
  return { ...filled, rows };
}

export interface PlanComputation {
  /** Contract gross (tender sum before ROT). */
  gross: number;
  /** Planned ROT deduction on the contract. */
  rot: number;
  /** What the customer pays (after ROT) – the stages must sum to this. */
  afterRot: number;
  /** After-ROT amount per stage, same order as rows. */
  stageAfterRot: number[];
  /** Sum of the stages' after-ROT amounts. */
  allocated: number;
  /** afterRot − allocated (positive: left to distribute; negative: exceeded). */
  remaining: number;
  hasRot: boolean;
}

/** Percent rows: whole kronor with the rounding remainder on the last row (as the guided plan). */
const percentAmounts = (rows: PlanRow[], target: number) => {
  const totalKronor = Math.round(target);
  const amounts = rows.map(row => Math.round((parsePercent(row.percent) / 100) * totalKronor));
  if (amounts.length > 0) {
    const prior = amounts.slice(0, -1).reduce((sum, amount) => sum + amount, 0);
    amounts[amounts.length - 1] = totalKronor - prior;
  }
  return amounts;
};

export function computePlan(input: PlanState): PlanComputation {
  const state = withAutofill(input);
  if (state.pricingMode === 'bottom_up') {
    const grossRows = state.rows.map(row => parseKronor(row.amount));
    const rotRows = state.rows.map(row => parseKronor(row.rotAmount));
    const stageAfterRot = grossRows.map((gross, index) => gross - rotRows[index]);
    const gross = grossRows.reduce((sum, value) => sum + value, 0);
    const rot = rotRows.reduce((sum, value) => sum + value, 0);
    const afterRot = gross - rot;
    return { gross, rot, afterRot, stageAfterRot, allocated: afterRot, remaining: 0, hasRot: rot !== 0 };
  }
  const gross = parseKronor(state.total);
  const afterRot = state.afterRot.trim() === '' ? gross : parseKronor(state.afterRot);
  const rot = gross - afterRot;
  const stageAfterRot = state.distribution === 'percent'
    ? percentAmounts(state.rows, afterRot)
    : state.rows.map(row => parseKronor(row.amount));
  const allocated = stageAfterRot.reduce((sum, value) => sum + value, 0);
  return { gross, rot, afterRot, stageAfterRot, allocated, remaining: afterRot - allocated, hasRot: rot !== 0 };
}

/**
 * What the allocation indicator says (top_down). "Complete" only when the
 * stages really add up: the sum matches the amount after ROT (or the percents
 * make 100 %) and every stage has an amount. `computePlan` puts the rounding
 * remainder of a percent plan on the last stage, so its `remaining` is 0 even
 * when the percents do not add up or a new stage is empty – the indicator must
 * not use it.
 */
export type AllocationStatus =
  | { kind: 'pending' }
  | { kind: 'invalid' }
  | { kind: 'complete' }
  | { kind: 'missingStage'; number: number }
  | { kind: 'remaining'; amount: number; percent?: number }
  | { kind: 'exceeded'; amount: number; percent?: number };

export interface Allocation {
  status: AllocationStatus;
  /** Kronor the stages actually cover, for "Fördelat" and the progress bar. */
  allocated: number;
}

export function allocationStatus(input: PlanState): Allocation {
  const state = withAutofill(input);
  const plan = computePlan(state);
  const target = plan.afterRot;
  if (!isWhole(target) || target <= 0) return { status: { kind: 'pending' }, allocated: 0 };

  if (state.distribution === 'percent') {
    const percents = state.rows.map(row => parsePercent(row.percent));
    if (percents.some(value => !Number.isFinite(value))) return { status: { kind: 'invalid' }, allocated: 0 };
    const percentSum = roundPercent(percents.reduce((sum, value) => sum + value, 0));
    const allocated = Math.round((percentSum / 100) * target);
    const left = roundPercent(100 - percentSum);
    if (Math.abs(left) > 0.01) {
      return left > 0
        ? { status: { kind: 'remaining', amount: target - allocated, percent: left }, allocated }
        : { status: { kind: 'exceeded', amount: allocated - target, percent: -left }, allocated };
    }
    const empty = percents.findIndex(value => value <= 0);
    if (empty >= 0) return { status: { kind: 'missingStage', number: empty + 1 }, allocated };
    return { status: { kind: 'complete' }, allocated: target };
  }

  const amounts = state.rows.map(row => parseKronor(row.amount));
  if (amounts.some(value => !Number.isFinite(value))) return { status: { kind: 'invalid' }, allocated: 0 };
  const allocated = amounts.reduce((sum, value) => sum + value, 0);
  if (allocated !== target) {
    return allocated < target
      ? { status: { kind: 'remaining', amount: target - allocated }, allocated }
      : { status: { kind: 'exceeded', amount: allocated - target }, allocated };
  }
  const empty = amounts.findIndex(value => value <= 0 || !isWhole(value));
  if (empty >= 0) return { status: { kind: 'missingStage', number: empty + 1 }, allocated };
  return { status: { kind: 'complete' }, allocated };
}

export interface PlanMessage {
  key: string;
  params?: Record<string, string | number>;
}

export interface PlanErrors {
  messages: PlanMessage[];
  total?: boolean;
  afterRot?: boolean;
  rows: Record<string, { description?: boolean; amount?: boolean; rotAmount?: boolean }>;
}

export const hasPlanErrors = (errors: PlanErrors) => errors.messages.length > 0;

export function validatePlan(input: PlanState): PlanErrors {
  const state = withAutofill(input);
  const errors: PlanErrors = { messages: [], rows: {} };
  const add = (message: PlanMessage) => {
    if (!errors.messages.some(existing => existing.key === message.key)) errors.messages.push(message);
  };
  const rowError = (row: PlanRow, field: 'description' | 'amount' | 'rotAmount') => {
    errors.rows[row.key] = { ...errors.rows[row.key], [field]: true };
  };
  const plan = computePlan(state);

  if (state.rows.length < 1 || state.rows.length > MAX_PLAN_ROWS) add({ key: 'milestones.wizard.errCount' });
  state.rows.forEach(row => {
    if (!row.description.trim()) {
      rowError(row, 'description');
      add({ key: 'milestones.wizard.errDesc' });
    }
  });

  if (state.pricingMode === 'top_down') {
    const total = parseKronor(state.total);
    const afterRot = plan.afterRot;
    if (!isWhole(total) || !isWhole(afterRot)) {
      errors.total = !isWhole(total);
      errors.afterRot = !isWhole(afterRot);
      add({ key: 'milestones.wizard.errWholeKronor' });
      return errors;
    }
    if (total <= 0) {
      errors.total = true;
      add({ key: 'milestones.wizard.errTotal' });
      return errors;
    }
    if (plan.rot !== 0 && (Math.sign(plan.rot) !== Math.sign(total) || Math.abs(plan.rot) > Math.abs(total))) {
      errors.afterRot = true;
      add({ key: 'projectView.wizard.errRotDirection' });
      return errors;
    }
    if (state.distribution === 'percent') {
      const percents = state.rows.map(row => parsePercent(row.percent));
      if (percents.some(value => !Number.isFinite(value))) {
        state.rows.forEach((row, index) => { if (!Number.isFinite(percents[index])) rowError(row, 'amount'); });
        add({ key: 'milestones.wizard.errSumPercent', params: { sum: '–' } });
        return errors;
      }
      const sum = percents.reduce((total, value) => total + value, 0);
      if (Math.abs(sum - 100) > 0.01) {
        add({ key: 'milestones.wizard.errSumPercent', params: { sum: Math.round(sum * 100) / 100 } });
      }
    } else {
      state.rows.forEach((row, index) => {
        if (!isWhole(plan.stageAfterRot[index])) {
          rowError(row, 'amount');
          add({ key: 'milestones.wizard.errWholeKronor' });
        }
      });
    }
    state.rows.forEach((row, index) => {
      const amount = plan.stageAfterRot[index];
      if (isWhole(amount) && amount <= 0) {
        rowError(row, 'amount');
        add({ key: 'projectView.wizard.errNonZeroSameSign' });
      }
    });
    if (state.distribution === 'amount' && Number.isFinite(plan.allocated) && plan.remaining !== 0) {
      add({
        key: plan.hasRot ? 'projectView.wizard.errSumAfterRot' : 'milestones.wizard.errSumAmount',
        params: { sum: formatKronor(plan.allocated), target: formatKronor(afterRot) },
      });
    }
    return errors;
  }

  // bottom_up
  state.rows.forEach(row => {
    const gross = parseKronor(row.amount);
    const rot = parseKronor(row.rotAmount);
    if (!isWhole(gross) || gross <= 0) {
      rowError(row, 'amount');
      add({ key: isWhole(gross) ? 'milestones.wizard.errAmountRow' : 'milestones.wizard.errWholeKronor' });
    }
    if (!isWhole(rot)) {
      rowError(row, 'rotAmount');
      add({ key: 'milestones.wizard.errWholeKronor' });
    } else if (rot < 0 || (isWhole(gross) && rot > gross)) {
      rowError(row, 'rotAmount');
      add({ key: 'projectView.wizard.errRotDirection' });
    }
  });
  return errors;
}

/**
 * Even split of the amount after ROT (top_down). With two or more stages the
 * last one is left empty, so it is autofilled with the remainder and keeps
 * following later changes.
 */
export function splitEvenly(state: PlanState): PlanState {
  const plan = computePlan(state);
  const count = state.rows.length;
  if (count === 0 || !isWhole(plan.afterRot) || plan.afterRot <= 0) return state;
  const autofillLast = count >= 2;
  if (state.distribution === 'percent') {
    const each = Math.floor((10000 / count)) / 100;
    return {
      ...state,
      rows: state.rows.map((row, index) => ({
        ...row,
        percent: index < count - 1
          ? formatPercentInput(each)
          : autofillLast ? '' : formatPercentInput(100 - each * (count - 1)),
      })),
    };
  }
  const each = Math.floor(plan.afterRot / count);
  return {
    ...state,
    rows: state.rows.map((row, index) => ({
      ...row,
      amount: index < count - 1
        ? String(each)
        : autofillLast ? '' : String(plan.afterRot - each * (count - 1)),
    })),
  };
}

/**
 * Switching kr ⇄ % keeps the distribution: derive the other representation
 * first. An autofilled last stage stays autofilled in the other unit.
 */
export function switchDistribution(state: PlanState, next: DistributionMode): PlanState {
  if (state.distribution === next || state.pricingMode !== 'top_down') return { ...state, distribution: next };
  const plan = computePlan(state);
  const autofilled = autofillRemainder(state) !== null;
  const keepEmpty = (index: number) => autofilled && index === state.rows.length - 1;
  if (next === 'percent') {
    return {
      ...state,
      distribution: next,
      rows: state.rows.map((row, index) => {
        const amount = plan.stageAfterRot[index];
        const percent = plan.afterRot > 0 && Number.isFinite(amount) ? Math.round((amount / plan.afterRot) * 10000) / 100 : 0;
        return { ...row, percent: amount && !keepEmpty(index) ? formatPercentInput(percent) : '' };
      }),
    };
  }
  return {
    ...state,
    distribution: next,
    rows: state.rows.map((row, index) => ({
      ...row,
      amount: plan.stageAfterRot[index] && !keepEmpty(index) ? String(plan.stageAfterRot[index]) : '',
    })),
  };
}

export interface MilestonePlanPayload {
  pricingMode: PricingMode;
  contractAmount: string;
  afterRotAmount: string;
  milestones: Array<{ id?: string; description: string; amount: number; rotAmount?: number; endDate?: string }>;
}

/** Payload for `milestoneApi.replaceMilestonePlan` (amounts in kronor). */
export function toPlanPayload(input: PlanState): MilestonePlanPayload {
  const state = withAutofill(input);
  const plan = computePlan(state);
  return {
    pricingMode: state.pricingMode,
    contractAmount: String(plan.gross),
    afterRotAmount: String(plan.afterRot),
    milestones: state.rows.map((row, index) => ({
      ...(row.id ? { id: row.id } : {}),
      description: row.description.trim(),
      ...(state.pricingMode === 'bottom_up'
        ? { amount: parseKronor(row.amount), rotAmount: parseKronor(row.rotAmount) }
        : { amount: plan.stageAfterRot[index] }),
      endDate: row.endDate,
    })),
  };
}

/** Snapshot used to tell whether the plan differs from what was last saved. */
export const planFingerprint = (state: PlanState) =>
  JSON.stringify({ ...toPlanPayload(state), rows: state.rows.map(row => row.id ?? null) });
